import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { UAParser } from 'ua-parser-js';

/**
 * shardcn - https://ui.shadcn.com/docs/installation
 * @param inputs
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 자사(내부) 도메인 목록. 서브도메인(예: gangnam.dztraining.co.kr)도 내부로 취급한다.
 * 외부 광고 도메인이 추가되면 이 목록 기준으로 외부로 판별된다.
 */
export const SELF_DOMAINS = ['dztraining.co.kr'];

/**
 * href가 외부 링크인지 판별한다. (광고 영역에서 외부 링크에 rel="sponsored"를 붙이기 위한 용도)
 * - 상대경로('/...')나 절대 URL로 파싱되지 않는 값 → 내부(false)
 * - http(s) 절대 URL이면서 호스트가 SELF_DOMAINS(서브도메인 포함)에 속하면 내부(false)
 * - 그 외(타 도메인, mailto/tel 등) → 외부(true)
 */
export function isExternalHref(href: string): boolean {
  if (!href) return false;
  if (href.startsWith('/')) return false;

  try {
    const url = new URL(href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return true;
    const host = url.host.toLowerCase();
    return !SELF_DOMAINS.some(
      (domain) => host === domain || host.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
}

export function getBranchIdFromSlug(slug: string) {
  const parts = slug.split('-');
  return Number(parts[parts.length - 1]);
}

export function slugify(name: string, id: number) {
  return `${encodeURIComponent(name)
    .normalize('NFD') // 한글 자모 분리
    .replace(/[\u0300-\u036f]/g, '') // 발음 구분자 제거
    .replace(/\s+/g, '-') // 스페이스 → 대시
    .toLowerCase()
    // 쉼표·물음표 같은 ASCII 기호의 인코딩(%2c·%3f)만 대문자로 되돌린다. 구글은 %2c 와 %2C 를 다른
    // 주소로 보고, canonical 을 %2C 로 읽어 사이트맵의 %2c 주소를 '대체 페이지'로 분류했다(2026-10-21).
    // 한글 바이트(%80~%ff)는 구글이 대소문자 구분 없이 같은 주소로 보므로 기존 주소를 바꾸지 않는다.
    .replace(/%[0-7][0-9a-f]/g, (escape) => escape.toUpperCase())}-${id}`; // ID 덧붙이기
}

// 2) “숫자+호선” 패턴만 뽑아내는 함수 (기존)
export function parseLineType(address: string): string {
  const match = address.match(/(\d+호선)/);
  return match ? match[1] : '';
}

export function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
//타이틀을 URL로 변환하는 함수(슬러그화) 사용
//공백은 -로,
//특수문자/기호는 제거 또는 변환
//ex)<Linkkey={item.title + item.date + idx} href={`/support/contact/${toSlug(item.title)}`}>
export function toSlug(title: string) {
  return title
    .trim()
    .replace(/\s+/g, '-') // 공백을 -로
    .replace(/[^\w가-힣-]+/g, '') // 한글, 영문, 숫자, -만 허용
    .replace(/-+/g, '-') // 연속된 -는 하나로
    .toLowerCase();
}

// 슬러그에서 id 추출
export function fromSlug(slug: string): number | null {
  const match = slug.match(/(\d+)(?!.*\d)/);
  return match ? Number(match[1]) : null;
}

export function formatPeopleCount(count: number): string {
  if (count >= 10000) {
    // 만 단위
    const man = Math.floor(count / 10000);
    return `${man}만명+`;
  } else if (count >= 1000) {
    // 천 단위
    const chun = Math.floor(count / 1000);
    return `${chun}천명+`;
  } else if (count >= 100) {
    // 백 단위
    const baek = Math.floor(count / 100);
    return `${baek}백명+`;
  } else {
    // 그 외
    return `${count}명`;
  }
}

export function getLast<T>(items: Array<T>): T | undefined {
  return items.at(-1);
}

export function detectDevice() {
  const userAgent =
    typeof window !== 'undefined' ? window.navigator.userAgent : '';
  const parser = new UAParser(userAgent);
  const result = parser.getResult();
  const isIOS = result.os.name === 'iOS';
  const isAndroid = result.os.name === 'Android';

  return {
    isIOS,
    isAndroid,
    result,
  };
}
export function formatKoreanCurrency(num: number): string {
  const man = Math.floor(num / 10000);
  return `${man}만원`;
}
// 이슈  : ios도 android도 아닐 경우의 url은 어디로 ?
export function getDororoAppUrl() {
  const { isIOS, isAndroid } = detectDevice();
  return isIOS
    ? 'https://apps.apple.com/app/id6740759511'
    : isAndroid
      ? 'https://play.google.com/store/apps/details?id=kr.co.dororo'
      : 'https://play.google.com/store/apps/details?id=kr.co.dororo';
}

export const convertToHyphen = (str: string): string => str.replace(/_/g, '-');

export function stripHtmlTags(input: string): string {
  return input.replace(/<\/?[^>]+(>|$)/g, '');
}

/**
 * 메타 description 용 한 줄 요약. 학원 소개글처럼 줄바꿈·이모지·글머리 기호가 섞인 원문을
 * 그대로 넣으면 검색 요약이 지저분해져, 기호를 걷어내고 한 줄로 이은 뒤 maxLength 에서 자른다.
 * (tsconfig target 이 ES5 라 정규식 u 플래그 대신 서로게이트 쌍으로 이모지를 거른다)
 */
export function toMetaDescription(
  text: string | null | undefined,
  maxLength = 150,
): string {
  if (!text) return '';
  const oneLine = text
    .replace(/<\/?[^>]+(>|$)/g, ' ')
    .replace(/[☀-➿]|[\uD83C-\uD83E][\uDC00-\uDFFF]|️|‍/g, '')
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s\-–—•·*▶►■□◆◇○●※]+/, '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (oneLine.length <= maxLength) return oneLine;
  const cut = oneLine.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/**
 * HTML에서 텍스트만 뽑는다(글 카드 요약용).
 * 서버 렌더에서도 호출되므로 DOMParser(브라우저 전용)를 쓰지 않는다. 서버·클라이언트가
 * 같은 문자열을 내야 하이드레이션이 맞는다. 공백은 한 칸으로 합친다.
 */
export function extractTextOnly(input: string): string {
  return (
    input
      // script, style 제거
      .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (_, code) =>
        String.fromCodePoint(parseInt(code, 16)),
      )
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      // &amp;는 마지막에 풀어야 &amp;lt; 같은 문자열이 두 번 풀리지 않는다.
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim()
  );
}
